import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/api/client'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useCurrency } from '@/contexts/CurrencyContext'
import { toastHelpers } from '@/lib/toast-helpers'
import { FAILED_OUTCOME_LABEL, ISSUE_LABEL } from '@/lib/riderLinks'
import type { DeliveryIssue, FailedOutcome, RiderProgressRequest, RiderTicket } from '@/types/rider'

// What happened at the door (F-DLV-N1). Delivered (optionally with an issue)
// or couldn't deliver (with a reason). The phone's position at the tap is
// sent when the browser gives it within a few seconds — it is never required.

function currentPosition(): Promise<GeolocationPosition | null> {
  if (!('geolocation' in navigator)) return Promise.resolve(null)
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(resolve, () => resolve(null), {
      enableHighAccuracy: true,
      timeout: 5_000,
      maximumAge: 60_000,
    })
  })
}

export function RiderOutcomeDialog({
  mode,
  onClose,
  ticket,
  due,
}: {
  mode: 'delivered' | 'failed' | null
  onClose: (done: boolean) => void
  ticket: RiderTicket
  due: number
}) {
  const qc = useQueryClient()
  const { formatCurrency } = useCurrency()
  const [issue, setIssue] = useState<DeliveryIssue | null>(null)
  const [reason, setReason] = useState<FailedOutcome | null>(null)
  const [note, setNote] = useState('')

  useEffect(() => {
    if (mode) {
      // Delivered with money still owed is a short payment unless the rider
      // says otherwise — pre-pick it so it isn't silently lost.
      setIssue(mode === 'delivered' && due > 0 ? 'short_paid' : null)
      setReason(null)
      setNote('')
    }
  }, [mode, due])

  const save = useMutation({
    mutationFn: async () => {
      const pos = await currentPosition()
      const body: RiderProgressRequest =
        mode === 'delivered'
          ? { status: 'delivered', outcome: issue ? 'delivered_with_issue' : 'delivered', issue: issue ?? undefined, note }
          : { status: 'failed', outcome: reason!, note }
      if (pos) {
        body.lat = pos.coords.latitude
        body.lng = pos.coords.longitude
        body.accuracy_m = pos.coords.accuracy
      }
      return apiClient.updateRiderAssignment(ticket.assignment_id!, body)
    },
    onSuccess: () => {
      toastHelpers.success(mode === 'delivered' ? 'Delivered' : 'Logged — bring the order back to the restaurant')
      qc.invalidateQueries({ queryKey: ['rider'] })
      onClose(true)
    },
    onError: (err: unknown) => toastHelpers.error('Could not save', (err as { message?: string })?.message || 'Please try again.'),
  })

  const noteNeeded = (mode === 'failed' && reason === 'other') || (mode === 'delivered' && issue === 'other')
  const canSubmit = (mode === 'delivered' || reason !== null) && (!noteNeeded || note.trim() !== '')

  return (
    <Dialog open={mode !== null} onOpenChange={(o) => !o && onClose(false)}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{mode === 'delivered' ? 'Delivered' : 'Couldn’t deliver'}</DialogTitle>
          <DialogDescription>Order #{ticket.order_number}</DialogDescription>
        </DialogHeader>

        {mode === 'delivered' ? (
          <div className="space-y-3">
            {due > 0 && (
              <p className="rounded-md bg-amber-50 px-2 py-1.5 text-sm text-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
                {formatCurrency(due)} is still unpaid. Record the payment first if the customer paid.
              </p>
            )}
            <div className="space-y-1.5">
              <Label>Anything wrong?</Label>
              <div className="grid grid-cols-2 gap-2">
                <Button type="button" variant={issue === null ? 'default' : 'outline'} onClick={() => setIssue(null)}>
                  All good
                </Button>
                {(Object.keys(ISSUE_LABEL) as DeliveryIssue[]).map((k) => (
                  <Button key={k} type="button" variant={issue === k ? 'default' : 'outline'} onClick={() => setIssue(k)}>
                    {ISSUE_LABEL[k]}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-1.5">
            <Label>What happened?</Label>
            <div className="grid gap-2">
              {(Object.keys(FAILED_OUTCOME_LABEL) as FailedOutcome[]).map((k) => (
                <Button
                  key={k}
                  type="button"
                  className="justify-start"
                  variant={reason === k ? 'default' : 'outline'}
                  onClick={() => setReason(k)}
                >
                  {FAILED_OUTCOME_LABEL[k]}
                </Button>
              ))}
            </div>
          </div>
        )}

        <div className="space-y-1.5">
          <Label htmlFor="rider-outcome-note">Note {noteNeeded ? '' : '(optional)'}</Label>
          <Textarea id="rider-outcome-note" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onClose(false)}>
            Cancel
          </Button>
          <Button onClick={() => save.mutate()} disabled={!canSubmit || save.isPending}>
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
